import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Calendar, MapPin, Clock, User, DollarSign, ChevronLeft, ChevronRight, AlertCircle, RefreshCw, History, Download, Play, ExternalLink, Facebook, Instagram } from 'lucide-react';
import { fetchLeiloes, getLeilaoImageUrl } from '../services/api';

const Home = () => {
  const navigate = useNavigate();
  const [leiloes, setLeiloes] = useState([]);
  const [leiloesMesAtual, setLeiloesMesAtual] = useState([]);
  const [leiloesProximos, setLeiloesProximos] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadLeiloes();
  }, []);

  // Função para parsear data no formato MM/DD/YYYY
  const parseDate = (dateString) => {
    try {
      const [month, day, year] = dateString.split('/');
      return new Date(year, month - 1, day);
    } catch {
      return new Date();
    }
  };

  // Função para obter o nome do mês
  const getMonthName = (date) => {
    return date.toLocaleDateString('pt-BR', { month: 'long' });
  };

  // Função para agrupar leilões por mês
  const groupByMonth = (leiloesList) => {
    const grouped = {};
    leiloesList.forEach(leilao => {
      const date = parseDate(leilao.DATLEI);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = getMonthName(date);
      const year = date.getFullYear();
      
      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          monthName: monthName.charAt(0).toUpperCase() + monthName.slice(1),
          year,
          leiloes: []
        };
      }
      grouped[monthKey].leiloes.push(leilao);
    });
    
    // Ordenar por chave (ano-mês)
    return Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => ({ key, ...value }));
  };

  const loadLeiloes = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await fetchLeiloes();
      
      // Filtra leilões ativos e ordena por DATLEI (mais próximo primeiro)
      const leiloesAtivos = data
        .filter(leilao => leilao.ATIVOX === 'S')
        .sort((a, b) => {
          const dateA = parseDate(a.DATLEI);
          const dateB = parseDate(b.DATLEI);
          return dateA - dateB;
        });
      
      setLeiloes(leiloesAtivos);
      
      // Separar leilões próximos (30 dias) e futuros
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const limite30dias = new Date(now);
      limite30dias.setDate(limite30dias.getDate() + 30);
      
      const mesAtual = [];
      const proximos = [];
      
      leiloesAtivos.forEach(leilao => {
        const leilaoDate = parseDate(leilao.DATLEI);
        leilaoDate.setHours(0, 0, 0, 0);
        
        if (leilaoDate >= now && leilaoDate <= limite30dias) {
          // Leilões dentro dos próximos 30 dias → carrossel
          mesAtual.push(leilao);
        } else if (leilaoDate > limite30dias) {
          // Leilões após 30 dias → agenda
          proximos.push(leilao);
        }
      });
      
      setLeiloesMesAtual(mesAtual);
      setLeiloesProximos(proximos);
      
    } catch (err) {
      console.error('Erro ao carregar leilões:', err);
      setError(err.message || 'Erro ao carregar leilões');
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % leiloesMesAtual.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + leiloesMesAtual.length) % leiloesMesAtual.length);
  };

  const formatDate = (dateString) => {
    try {
      // A API envia no formato MM/DD/YYYY (formato americano)
      const [month, day, year] = dateString.split('/');
      return new Date(year, month - 1, day).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const handleImageError = (e, leilaoId) => {
    e.target.style.display = 'none';
    e.target.nextSibling.style.display = 'flex';
  };

  const navigateToLeilao = (leilaoId) => {
    navigate(`/leilao/${leilaoId}`);
  };

  // Função para abrir catálogo - abre diretamente a URL do S3
  const openCatalogo = (urlCatalogo) => {
    console.log('=== DEBUG CATÁLOGO ===');
    console.log('URL recebida:', urlCatalogo);
    
    if (urlCatalogo) {
      // Limpar a URL removendo caracteres especiais e quebras de linha
      const cleanUrl = urlCatalogo.trim().replace(/[\r\n]/g, '');
      console.log('URL limpa:', cleanUrl);
      
      // Abre diretamente a URL (seja S3 ou qualquer outra)
      window.open(cleanUrl, '_blank', 'noopener,noreferrer');
      console.log('✅ Abrindo URL do catálogo');
    } else {
      alert('Catálogo não disponível para este leilão.');
    }
    console.log('=== FIM DEBUG CATÁLOGO ===');
  };

  // Função para abrir transmissão
  const openTransmissao = (leilao) => {
    // Verifica se há links de transmissão específicos
    if (leilao.LINKTRANSMISSAO1) {
      window.open(leilao.LINKTRANSMISSAO1, '_blank', 'noopener,noreferrer');
    } else if (leilao.LINKTRANSMISSAO2) {
      window.open(leilao.LINKTRANSMISSAO2, '_blank', 'noopener,noreferrer');
    } else if (leilao.TRANSMISSAO) {
      // Se não há link direto, pesquisa no YouTube/Google baseado na descrição
      const searchQuery = encodeURIComponent(`${leilao.TRANSMISSAO} ${leilao.LEILAO} ao vivo`);
      window.open(`https://www.youtube.com/results?search_query=${searchQuery}`, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[rgb(25,42,86)] mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando leilões...</p>
          </div>
        </div>
      </div>
    );
  }

  // Card para quando não há leilões ativos (sem erro de API)
  if (!loading && !error && leiloes.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-gray-50 text-gray-900 py-20 border-b-4 border-[rgb(25,42,86)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_knorr-auction-proxy/artifacts/ud6gmi7h_Screenshot_7-removebg-preview.png"
                  alt="Knorr Leilões"
                  className="h-20 md:h-28 w-auto"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <p className="text-2xl md:text-3xl font-black mb-8 max-w-4xl mx-auto leading-relaxed text-[rgb(25,42,86)]">
                Desde <span className="text-[rgb(25,42,86)] font-black">1960</span> realizando leilões com{' '}
                <span className="text-[rgb(25,42,86)] font-black">tradição</span>,{' '}
                <span className="text-[rgb(25,42,86)] font-black">modernidade</span> e{' '}
                <span className="text-[rgb(25,42,86)] font-black">eficiência</span>
              </p>
            </div>
          </div>
        </section>

        {/* Card de Próximos Leilões */}
        <div className="max-w-2xl mx-auto px-4 py-16">
          <Card className="border-2 border-[rgb(25,42,86)] shadow-xl overflow-hidden">
            <div className="bg-[rgb(25,42,86)] py-8 px-6">
              <div className="flex justify-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_knorr-auction-proxy/artifacts/ud6gmi7h_Screenshot_7-removebg-preview.png"
                  alt="Knorr Leilões"
                  className="h-24 w-auto bg-white rounded-full p-3"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </div>
            <CardContent className="text-center py-12 px-8 bg-white">
              <Calendar className="h-16 w-16 text-[rgb(238,29,37)] mx-auto mb-6" />
              <h2 className="text-2xl md:text-3xl font-bold text-[rgb(25,42,86)] mb-4">
                Em breve você verá nossos próximos leilões
              </h2>
              <p className="text-gray-600 text-lg mb-8">
                Estamos preparando novos eventos especiais para você. Fique atento às nossas redes sociais!
              </p>
              <div className="flex justify-center space-x-4">
                <a 
                  href="https://www.facebook.com/knorrleiloes" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)]/90 text-white px-6 py-3 rounded-lg flex items-center space-x-2 transition-colors"
                >
                  <Facebook className="h-5 w-5" />
                  <span>Facebook</span>
                </a>
                <a 
                  href="https://www.instagram.com/knorrleiloes" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-[rgb(238,29,37)] hover:bg-[rgb(238,29,37)]/90 text-white px-6 py-3 rounded-lg flex items-center space-x-2 transition-colors"
                >
                  <Instagram className="h-5 w-5" />
                  <span>Instagram</span>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Tratamento de erro real da API
  if (error && leiloes.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-gray-50 text-gray-900 py-20 border-b-4 border-[rgb(25,42,86)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_knorr-auction-proxy/artifacts/ud6gmi7h_Screenshot_7-removebg-preview.png"
                  alt="Knorr Leilões"
                  className="h-20 md:h-28 w-auto"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <p className="text-2xl md:text-3xl font-black mb-8 max-w-4xl mx-auto leading-relaxed text-[rgb(25,42,86)]">
                Desde <span className="text-[rgb(25,42,86)] font-black">1960</span> realizando leilões com{' '}
                <span className="text-[rgb(25,42,86)] font-black">tradição</span>,{' '}
                <span className="text-[rgb(25,42,86)] font-black">modernidade</span> e{' '}
                <span className="text-[rgb(25,42,86)] font-black">eficiência</span>
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 py-16">
          <Alert className="border-[rgb(238,29,37)] bg-red-50">
            <AlertCircle className="h-4 w-4 text-[rgb(238,29,37)]" />
            <AlertDescription className="text-[rgb(238,29,37)]">
              {error}
            </AlertDescription>
          </Alert>
          
          <div className="text-center mt-8">
            <Button 
              onClick={loadLeiloes}
              className="bg-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)]/90 flex items-center space-x-2 font-bold"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Tentar Novamente</span>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gray-50 text-gray-900 py-20 border-b-4 border-[rgb(25,42,86)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_knorr-auction-proxy/artifacts/ud6gmi7h_Screenshot_7-removebg-preview.png"
                alt="Knorr Leilões"
                className="h-20 md:h-28 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            <p className="text-2xl md:text-3xl font-black mb-8 max-w-4xl mx-auto leading-relaxed text-[rgb(25,42,86)]">
              Desde <span className="text-[rgb(25,42,86)] font-black">1960</span> realizando leilões com{' '}
              <span className="text-[rgb(25,42,86)] font-black">tradição</span>,{' '}
              <span className="text-[rgb(25,42,86)] font-black">modernidade</span> e{' '}
              <span className="text-[rgb(25,42,86)] font-black">eficiência</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-[rgb(238,29,37)] hover:bg-[rgb(238,29,37)]/90 text-white px-8 py-3 text-lg font-bold transition-colors duration-200 shadow-lg"
                onClick={() => {
                  document.getElementById('leiloes-ativos')?.scrollIntoView({ 
                    behavior: 'smooth' 
                  });
                }}
              >
                Nossos Leilões
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-[rgb(25,42,86)] text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white px-8 py-3 text-lg font-bold transition-colors duration-200"
                onClick={() => navigate('/history')}
              >
                <History className="h-5 w-5 mr-2" />
                Nossa História
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Nossos Leilões - Carrossel (Próximos 30 dias) - Só mostra se houver leilões */}
      {leiloesMesAtual.length > 0 && (
        <section id="leiloes-ativos" className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-black text-[rgb(25,42,86)] mb-4">
                Próximos Leilões
              </h2>
              <p className="text-lg text-gray-700 max-w-2xl mx-auto font-semibold">
                Confira os leilões dos próximos 30 dias
              </p>
            </div>

            <div className="relative">
              {/* Carrossel */}
              <div className="overflow-hidden rounded-lg">
                <div 
                  className="flex transition-transform duration-300 ease-in-out"
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {leiloesMesAtual.map((leilao) => (
                    <div key={leilao.ID} className="w-full flex-shrink-0 px-4">
                      <Card className="border-2 border-gray-200 hover:border-[rgb(25,42,86)] transition-colors duration-200">
                        <div className="relative">
                          {/* Imagem do Leilão - 1250x400 */}
                          <div className="w-full bg-gray-100 rounded-t-lg overflow-hidden" style={{aspectRatio: '1250/400'}}>
                            <img
                              src={getLeilaoImageUrl(leilao.ID)}
                              alt={leilao.LEILAO}
                              className="w-full h-full object-contain rounded-t-lg"
                              onError={(e) => handleImageError(e, leilao.ID)}
                            />
                            <div className="hidden w-full h-full items-center justify-center bg-[rgb(25,42,86)]/10">
                              <div className="text-center">
                                <Calendar className="h-12 w-12 text-[rgb(25,42,86)] mx-auto mb-2" />
                                <p className="text-[rgb(25,42,86)] font-semibold">
                                  {leilao.LEILAO}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          <Badge className="absolute top-4 right-4 bg-[rgb(238,29,37)] hover:bg-[rgb(238,29,37)]/90">
                            ATIVO
                          </Badge>
                        </div>

                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-xl text-[rgb(25,42,86)] line-clamp-2 flex-1 font-black">
                              {leilao.LEILAO}
                            </CardTitle>
                            
                            {/* Ícones de Ação */}
                            <div className="flex items-center space-x-2 ml-3">
                              {/* Ícone do Catálogo */}
                              {leilao.URLCATALOGO && (
                                <div className="flex items-center space-x-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)]/10 font-bold"
                                    onClick={() => {
                                      console.log('URL original do catálogo:', leilao.URLCATALOGO);
                                      console.log('Tipo da URL:', typeof leilao.URLCATALOGO);
                                      console.log('URL em hex:', Array.from(leilao.URLCATALOGO).map(char => char.charCodeAt(0).toString(16)).join(' '));
                                      openCatalogo(leilao.URLCATALOGO);
                                    }}
                                    title="Baixar Catálogo"
                                  >
                                    <Download className="h-4 w-4" />
                                  </Button>
                                  <span className="text-xs text-[rgb(25,42,86)] font-bold">
                                    Catálogo
                                  </span>
                                </div>
                              )}
                              
                              {/* Ícone da Transmissão */}
                              {(leilao.TRANSMISSAO || leilao.LINKTRANSMISSAO1 || leilao.LINKTRANSMISSAO2) && (
                                <div className="flex items-center space-x-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 text-[rgb(238,29,37)] hover:bg-[rgb(238,29,37)]/10"
                                    onClick={() => openTransmissao(leilao)}
                                    title="Ver Transmissão"
                                  >
                                    <Play className="h-4 w-4" />
                                  </Button>
                                  <span className="text-xs text-[rgb(238,29,37)] font-medium">
                                    Ao Vivo
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardHeader>

                        <CardContent className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4 text-[rgb(25,42,86)]" />
                              <span className="text-sm text-gray-600">
                                {formatDate(leilao.DATLEI)}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Clock className="h-4 w-4 text-[rgb(25,42,86)]" />
                              <span className="text-sm text-gray-600">
                                {leilao.HORA_INICIO}h
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <MapPin className="h-4 w-4 text-[rgb(25,42,86)]" />
                              <span className="text-sm text-gray-600">
                                {leilao.CIDADE}, {leilao.ESTADO}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <User className="h-4 w-4 text-[rgb(25,42,86)]" />
                              <span className="text-sm text-gray-600">
                                {leilao.LEILOE}
                              </span>
                            </div>
                          </div>

                          <div className="border-t pt-4">
                            <div className="flex items-center space-x-2 mb-2">
                              <DollarSign className="h-4 w-4 text-[rgb(238,29,37)]" />
                              <span className="text-sm font-semibold text-gray-800">
                                {leilao.DESFIN}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">
                              Local: {leilao.ENDERE}
                            </p>
                            <p className="text-xs text-gray-500">
                              Transmissão: {leilao.TRANSMISSAO}
                            </p>
                            {leilao.OBSERVACOES && (
                              <p className="text-xs text-gray-500 mt-2 line-clamp-2">
                                {leilao.OBSERVACOES}
                              </p>
                            )}
                          </div>

                          <Button 
                            onClick={() => navigateToLeilao(leilao.ID)}
                            className="w-full bg-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)]/90 transition-colors duration-200 font-bold"
                          >
                            Ver Lotes
                          </Button>
                        </CardContent>
                      </Card>
                    </div>
                  ))}
                </div>
              </div>

              {/* Controles do Carrossel */}
              {leiloesMesAtual.length > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white rounded-full p-2 shadow-lg border border-gray-200 hover:border-[rgb(25,42,86)] transition-colors duration-200"
                  >
                    <ChevronLeft className="h-6 w-6 text-[rgb(25,42,86)]" />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white rounded-full p-2 shadow-lg border border-gray-200 hover:border-[rgb(25,42,86)] transition-colors duration-200"
                  >
                    <ChevronRight className="h-6 w-6 text-[rgb(25,42,86)]" />
                  </button>

                  {/* Indicadores */}
                  <div className="flex justify-center mt-6 space-x-2">
                    {leiloesMesAtual.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                          index === currentSlide 
                            ? 'bg-[rgb(25,42,86)]' 
                            : 'bg-gray-300 hover:bg-[rgb(25,42,86)]/50'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Seção Agenda - Próximos Leilões */}
      {leiloesProximos.length > 0 && (
        <section id={leiloesMesAtual.length === 0 ? "leiloes-ativos" : ""} className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-black text-[rgb(25,42,86)] mb-4">
                Agenda de Leilões
              </h2>
              <p className="text-lg text-gray-700 max-w-2xl mx-auto font-semibold">
                Confira os próximos leilões programados
              </p>
            </div>

            <div className="space-y-8">
              {groupByMonth(leiloesProximos).map((grupo) => (
                <div key={grupo.key} className="bg-white rounded-xl shadow-md overflow-hidden">
                  {/* Cabeçalho do Mês */}
                  <div className="bg-[rgb(25,42,86)] text-white px-6 py-4">
                    <h3 className="text-2xl font-bold flex items-center">
                      <Calendar className="h-6 w-6 mr-3" />
                      {grupo.monthName} de {grupo.year}
                    </h3>
                  </div>
                  
                  {/* Lista de Leilões do Mês */}
                  <div className="divide-y divide-gray-100">
                    {grupo.leiloes.map((leilao) => {
                      const leilaoDate = parseDate(leilao.DATLEI);
                      const dayOfWeek = leilaoDate.toLocaleDateString('pt-BR', { weekday: 'long' });
                      const day = leilaoDate.getDate();
                      
                      return (
                        <div 
                          key={leilao.ID} 
                          className="flex items-center p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => navigateToLeilao(leilao.ID)}
                        >
                          {/* Data */}
                          <div className="flex-shrink-0 w-20 text-center">
                            <div className="bg-[rgb(238,29,37)] text-white rounded-lg p-2">
                              <span className="text-3xl font-black block">{day}</span>
                              <span className="text-xs uppercase">{dayOfWeek.slice(0, 3)}</span>
                            </div>
                          </div>
                          
                          {/* Info do Leilão */}
                          <div className="flex-1 ml-4">
                            <h4 className="text-lg font-bold text-[rgb(25,42,86)] hover:text-[rgb(238,29,37)] transition-colors">
                              {leilao.LEILAO}
                            </h4>
                            <div className="flex items-center text-sm text-gray-600 mt-1 space-x-4">
                              {leilao.HORA_INICIO && (
                                <span className="flex items-center">
                                  <Clock className="h-4 w-4 mr-1" />
                                  {leilao.HORA_INICIO}h
                                </span>
                              )}
                              {leilao.CIDADE && (
                                <span className="flex items-center">
                                  <MapPin className="h-4 w-4 mr-1" />
                                  {leilao.CIDADE}{leilao.ESTADO ? ` - ${leilao.ESTADO}` : ''}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Ações */}
                          <div className="flex-shrink-0 flex items-center space-x-2">
                            {leilao.URLCATALOGO && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-[rgb(25,42,86)] border-[rgb(25,42,86)]"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openCatalogo(leilao.URLCATALOGO);
                                }}
                                title="Ver Catálogo"
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            )}
                            <ChevronRight className="h-5 w-5 text-gray-400" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Seção Redes Sociais */}
      <section className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-[rgb(25,42,86)] mb-4">
              Siga-nos nas Redes Sociais
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Fique por dentro das novidades, leilões e conteúdos exclusivos. 
              Conecte-se conosco e faça parte da nossa comunidade!
            </p>
          </div>

          <div className="flex flex-col sm:flex-row justify-center items-center gap-6">
            {/* Facebook */}
            <a
              href="https://www.facebook.com/eduardoknorrleiloes/"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-gradient-to-r from-[#1877f2] to-[#42a5f5] text-white rounded-lg p-6 w-full sm:w-80 hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            >
              <div className="flex items-center justify-center space-x-4">
                <div className="bg-white/20 rounded-full p-3 group-hover:bg-white/30 transition-colors duration-300">
                  <Facebook className="h-8 w-8" />
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-bold">Facebook</h3>
                  <p className="text-blue-100">@eduardoknorrleiloes</p>
                </div>
              </div>
            </a>

            {/* Instagram */}
            <a
              href="https://www.instagram.com/knorrleiloes/?hl=pt-br"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-gradient-to-r from-[#e4405f] via-[#f56040] to-[#fccc63] text-white rounded-lg p-6 w-full sm:w-80 hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            >
              <div className="flex items-center justify-center space-x-4">
                <div className="bg-white/20 rounded-full p-3 group-hover:bg-white/30 transition-colors duration-300">
                  <Instagram className="h-8 w-8" />
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-bold">Instagram</h3>
                  <p className="text-orange-100">@knorrleiloes</p>
                </div>
              </div>
            </a>
          </div>

          {/* Call-to-action adicional */}
          <div className="mt-12 p-6 bg-gradient-to-r from-[rgb(25,42,86)] to-[rgb(238,29,37)] rounded-lg text-white">
            <h3 className="text-xl font-bold mb-2">
              📱 Não perca nenhuma novidade!
            </h3>
            <p className="text-gray-100">
              Siga nossas redes sociais para acompanhar leilões ao vivo, bastidores e muito mais.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[rgb(25,42,86)] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-white p-3 rounded-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_knorr-auction-proxy/artifacts/ud6gmi7h_Screenshot_7-removebg-preview.png"
                alt="Knorr Leilões"
                className="h-12 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          </div>
          <h3 className="text-2xl font-black mb-2">KNORR LEILÕES</h3>
          <p className="text-gray-300">
            tradição, modernidade e eficiência
          </p>
          <div className="flex justify-center space-x-4 mt-4 text-sm">
            <a href="/privacidade" className="text-gray-300 hover:text-white transition-colors">
              Política de Privacidade
            </a>
            <span className="text-gray-500">|</span>
            <a href="/contact" className="text-gray-300 hover:text-white transition-colors">
              Contato
            </a>
          </div>
          <p className="text-sm text-gray-400 mt-4">
            © {new Date().getFullYear()} Knorr Leilões. Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home;